"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ITQANDropDownMinus, ITQANDropDownPlus } from "../../SVGIcons";
import FilterPanel from "./filter-panel";
import FilterCheckboxes from "./filter-panel/filter-checkboxes";
import FilterInput from "./filter-panel/filter-input";
import FilterTree from "./filter-panel/filter-tree";
import FilterToggle from "./filter-toggle";
import "./styles.css";
import ValueItem from "./value-item";

// Recursive function to filter the tree
const filterTree = (items, query) => {
  return items
    .map((item) => {
      const matchesParent = item.label
        .toLowerCase()
        .includes(query.toLowerCase());

      let filteredChildren = [];
      if (item.children && item.children.length > 0) {
        filteredChildren = filterTree(item.children, query);
      }

      return matchesParent
        ? { ...item, children: item.children || [] } // keep all children
        : filteredChildren.length > 0
        ? { ...item, children: filteredChildren } // keep only matching children
        : null;
    })
    .filter((item) => item != null);
};

export default function ITQANDropDown({
  placeholder,
  options: initialData,
  originalSelectedOptions: selectedOptions,
  optionsSelected,
  lang,
  fixedMenuPosition = false,
  returnAsArray = false,
  renderSelectedValues = false,
  fullWidth = false,
}) {
  const rootId = initialData?.[0]?.id;
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [hasAllChildren, setHasAllChildren] = useState(false);
  const [hasFirstChild, setHasFirstChild] = useState(false);
  const [checkedItems, setCheckedItems] = useState(new Set(selectedOptions));
  const [expandedItems, setExpandedItems] = useState(new Set([rootId]));
  const [dropDirection, setDropDirection] = useState("bottom"); // or "top"
  const containerRef = useRef(null);

  const togglePanel = () => {
    setIsOpen((prev) => {
      const next = !prev;

      if (!prev && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const panelHeight = 300; // or a more accurate height of your dropdown
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow >= panelHeight) {
          setDropDirection("bottom");
        } else if (spaceAbove >= panelHeight) {
          setDropDirection("top");
        } else {
          // fallback: choose the larger side
          setDropDirection(spaceBelow > spaceAbove ? "bottom" : "top");
        }
      }

      return next;
    });
  };

  const toggleCheckbox = useCallback(
    (id, children) => {
      setCheckedItems((prevCheckedItems) => {
        const newCheckedItems = new Set(prevCheckedItems);
        const isChecked = newCheckedItems.has(id);

        const toggleRecursively = (items, shouldCheck) => {
          items.forEach((item) => {
            if (shouldCheck) {
              newCheckedItems.add(item.id);
            } else {
              newCheckedItems.delete(item.id);
            }

            if (item.children && !hasFirstChild) {
              toggleRecursively(item.children, shouldCheck);
            }
          });
        };

        if (isChecked) {
          newCheckedItems.delete(id);
          if (children && hasAllChildren) toggleRecursively(children, false);
        } else {
          newCheckedItems.add(id);
          if (children && hasAllChildren) toggleRecursively(children, true);
        }

        if (returnAsArray) {
          optionsSelected([...newCheckedItems]);
        } else {
          optionsSelected([...newCheckedItems].join(", "));
        }

        return newCheckedItems;
      });
    },
    [hasAllChildren, hasFirstChild]
  );

  // console.log({ values: [...checkedItems].join(", ") });

  const toggleExpand = useCallback((id, children) => {
    setExpandedItems((prevExpandedItems) => {
      const newExpandedItems = new Set(prevExpandedItems);
      const isExpanded = newExpandedItems.has(id);

      const collapseRecursively = (items) => {
        items.forEach((item) => {
          newExpandedItems.delete(item.id);
          if (item.children) {
            collapseRecursively(item.children);
          }
        });
      };

      if (isExpanded) {
        newExpandedItems.delete(id);
        if (id !== undefined && children) {
          collapseRecursively(children);
        }
      } else {
        newExpandedItems.add(id);
      }

      return newExpandedItems;
    });
  }, []);

  const toggleAllChildrenFilter = () => {
    setHasAllChildren((prev) => {
      checkedItems.clear();
      return !prev;
    });
  };

  const toggleFirstChildFilter = () => {
    setHasFirstChild((prev) => {
      checkedItems.clear();
      return !prev;
    });
  };

  const handleFilterChange = (event) => {
    setQuery(event.target.value);
  };

  const filteredData = useMemo(() => {
    if (!debouncedQuery) return initialData;
    return filterTree(initialData, debouncedQuery);
  }, [debouncedQuery, initialData]);

  const renderItems = (items, isChild) => {
    if (!items || items.length <= 0) {
      return <div className="hitq-no-results">No results found..</div>;
    }

    return (
      <>
        {items.map((item) => (
          <div key={item.id}>
            <div className="hitq-item-wrapper">
              <label
                className={`hitq-item-label ${
                  isChild ? "hitq-child-item" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={checkedItems.has(item.id)}
                  onChange={() => toggleCheckbox(item.id, item.children)}
                  disabled={item.disabled}
                  className="hitq-checkbox-input checkbox"
                />
                <span className="hitq-item-label-text">{item.label}</span>
              </label>
              {item.children && item.children.length > 0 && !query && (
                <button
                  type="button"
                  className="hitq-expand-collapse-btn"
                  onClick={() => toggleExpand(item.id, item.children)}
                >
                  {expandedItems.has(item.id) ? (
                    <ITQANDropDownMinus className="dropdown-icon-p" />
                  ) : (
                    <ITQANDropDownPlus className="dropdown-icon-p" />
                  )}
                </button>
              )}
            </div>

            {item.children && item.children.length > 0 && query && (
              <div className="hitq-children-container">
                {renderItems(item.children, true)}
              </div>
            )}

            {item.children &&
              item.children.length > 0 &&
              expandedItems.has(item.id) &&
              !query && (
                <div className="hitq-children-container">
                  {renderItems(item.children, true)}
                </div>
              )}
          </div>
        ))}
      </>
    );
  };

  const handleUncheck = (itemId) => {
    setCheckedItems((prevCheckedItems) => {
      const newCheckedItems = new Set(prevCheckedItems);

      newCheckedItems.delete(itemId);

      if (returnAsArray) {
        optionsSelected([...newCheckedItems]);
      } else {
        optionsSelected([...newCheckedItems].join(", "));
      }

      return newCheckedItems;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 800);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false); // <--- close panel if clicked outside
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const findCheckedItemById = (data, id) => {
    for (const item of data) {
      if (item.id === id) return item;
      if (item.children && item.children.length > 0) {
        const found = findCheckedItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={`${
        renderSelectedValues
          ? "d-flex align-items-center gap-2 flex-wrap"
          : "d-block"
      } ${fullWidth ? "w-100" : ""}`}
    >
      <div
        ref={containerRef}
        className={`hitq-container ${fullWidth ? "w-100" : ""}`}
      >
        <FilterToggle
          isOpen={isOpen}
          placeholder={window.sys_app_translate(placeholder, lang)}
          onClick={togglePanel}
        />
        {isOpen && (
          <FilterPanel
            closePanel={() => setIsOpen(false)}
            fixedMenuPosition={fixedMenuPosition}
            dropDirection={dropDirection}
          >
            <FilterInput
              value={query}
              handleFilterChange={handleFilterChange}
            />
            <FilterCheckboxes
              selectAllChildren={{
                value: hasAllChildren,
                toggleAllChildrenFilter,
              }}
              selectFirstChild={{
                value: hasFirstChild,
                toggleFirstChildFilter,
              }}
            />
            <FilterTree renderItems={() => renderItems(filteredData)} />
          </FilterPanel>
        )}
      </div>
      {renderSelectedValues && checkedItems && checkedItems.size > 0 && (
        <div className="d-flex align-items-center gap-1 flex-wrap hitq-checked-values">
          {[...checkedItems].map((checkedItem, index) => {
            const matchedItem = findCheckedItemById(initialData, checkedItem);

            return matchedItem ? (
              <ValueItem
                key={index}
                label={matchedItem.name}
                onDelete={() => handleUncheck(checkedItem)}
              />
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}
